import { useEffect, useRef, useState } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { MessageSquare } from "lucide-react";
import Header from "./components/Header";
import Footer from "./components/Footer";
import Home from "./pages/Home";
import ContactUsPage from "./pages/ContactUsPage";
import ExecutiveArchitectPage from "./pages/ExecutiveArchitectPage";
import ExecutiveInteriorPage from "./pages/ExecutiveInteriorPage";
import CustomizeYourProgramPage from "./pages/CustomizeYourProgramPage";
import JobsPage from "./pages/JobsPage";
import HireTalentPage from "./pages/HireTalentPage";
import EnrollPage from "./pages/EnrollPage";
import PartnerCollegeForm from "./pages/PartnerCollegeForm";
import TrainerApplicationForm from "./pages/TrainerApplicationForm";
// import Ipdiprogrampageredesign from "./pages/Ipdiprogrampageredesign";

function ScrollToHash() {
  const { hash, pathname } = useLocation();

  useEffect(() => {
    if (!hash) {
      return;
    }

    const targetId = decodeURIComponent(hash.slice(1));
    const scrollToTarget = () => {
      document.getElementById(targetId)?.scrollIntoView({ block: "start" });
    };

    const frame = window.requestAnimationFrame(scrollToTarget);
    return () => window.cancelAnimationFrame(frame);
  }, [hash, pathname]);

  return null;
}

const floatingWidgetCss = `
.floating-contact-widget {
  position: fixed;
  right: 24px;
  bottom: 89px;
  z-index: 9999;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
}

.floating-contact-menu {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  opacity: 0;
  transform: translateY(10px) scale(0.94);
  pointer-events: none;
  transition: opacity 0.2s ease, transform 0.2s ease;
}

/* Desktop/hover-capable devices only: hover or keyboard-focus opens the menu.
   Mobile deliberately does NOT use hover/focus-within here, because a tapped
   <a> keeps browser focus afterwards, which would keep the menu stuck open
   even after we toggle it closed via JS. Mobile uses .menu-open only. */
@media (hover: hover) and (pointer: fine) {
  .floating-contact-widget:hover .floating-contact-menu,
  .floating-contact-widget:focus-within .floating-contact-menu {
    opacity: 1;
    transform: translateY(0) scale(1);
    pointer-events: auto;
  }
}

.floating-contact-widget.menu-open .floating-contact-menu {
  opacity: 1;
  transform: translateY(0) scale(1);
  pointer-events: auto;
}

.floating-contact-item {
  position: relative;
  display: flex;
  align-items: center;
}

.floating-contact-action {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  // border-radius: 50%;
  background: transparent !important;
  // box-shadow: 0 6px 16px rgba(0, 0, 0, 0.18);
  border: none;
  color: #25d366;
  text-decoration: none;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.floating-contact-action img,
.floating-contact-action svg {
  width: 26px;
  height: 26px;
}

// .floating-contact-action:hover {
//   transform: scale(1.08);
//   box-shadow: 0 8px 20px rgba(0, 0, 0, 0.25);
// }

.floating-contact-label {
  position: absolute;
  right: 60px;
  
  white-space: nowrap;
  background: #c84b20;
  color: #fff;
  font-size: 12px;
  padding: 6px 10px;
  border-radius: 6px;
  opacity: 0;
  transform: translateX(6px);
  pointer-events: none;
  transition: opacity 0.15s ease, transform 0.15s ease;
}

.floating-contact-item:hover .floating-contact-label {
  opacity: 1;
  transform: translateX(0);
}

.floating-contact-chat {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 58px;
  height: 58px;
  border-radius: 50%;
  background: #c84b20;
  color: #fff;
  text-decoration: none;
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.2);
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}

// .floating-contact-chat:hover {
//   transform: scale(1.06);
//   box-shadow: 0 12px 24px rgba(0, 0, 0, 0.28);
// }

/* Mobile: widget hidden by default, shows only while scrolling UP.
   (Login/Register bar in Footer.jsx handles scroll-down show, as before) */
@media (max-width: 767px) {
  .floating-contact-widget {
    bottom: 24px;
    opacity: 0;
    transform: translateY(16px) scale(0.96);
    pointer-events: none;
    transition: opacity 0.25s ease, transform 0.25s ease;
  }

  .floating-contact-widget.is-visible-mobile {
    opacity: 1;
    transform: translateY(0) scale(1);
    pointer-events: auto;
  }

  /* Sub-menu only opens on tap (menu-open class), not on hover-emulation */
  .floating-contact-widget .floating-contact-menu {
    opacity: 0;
    transform: translateY(10px) scale(0.94);
    pointer-events: none;
    background: #ffffff;
    padding: 25px 10px 25px 10px;
    
    border-radius: 30px;
    transition: opacity 0.2s ease, transform 0.2s ease;

  }

  .floating-contact-widget.menu-open .floating-contact-menu {
    opacity: 1;
    transform: translateY(0) scale(1);
    pointer-events: auto;
     background: #ffffff;
  }
}

`;

function App() {
  // ── Mobile: show floating contact widget on scroll UP, hide on scroll DOWN ──
  const [showFloatingWidget, setShowFloatingWidget] = useState(false);
  const lastFloatingScrollY = useRef(0);

  // ── Mobile: WhatsApp/Call sub-menu opens only on tap (not on hover-emulation) ──
  const [contactMenuOpen, setContactMenuOpen] = useState(false);
  const widgetRef = useRef(null);

  useEffect(() => {
    lastFloatingScrollY.current = window.scrollY;

    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      if (currentScrollY < lastFloatingScrollY.current) {
        // scrolling up
        setShowFloatingWidget(true);
      } else if (currentScrollY > lastFloatingScrollY.current && currentScrollY > 80) {
        // scrolling down
        setShowFloatingWidget(false);
        setContactMenuOpen(false);
      }

      lastFloatingScrollY.current = currentScrollY;
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close the sub-menu if the user taps anywhere outside the widget
  useEffect(() => {
    if (!contactMenuOpen) return undefined;

    const handleOutsideClick = (event) => {
      if (widgetRef.current && !widgetRef.current.contains(event.target)) {
        setContactMenuOpen(false);
      }
    };

    document.addEventListener("touchstart", handleOutsideClick);
    document.addEventListener("click", handleOutsideClick);
    return () => {
      document.removeEventListener("touchstart", handleOutsideClick);
      document.removeEventListener("click", handleOutsideClick);
    };
  }, [contactMenuOpen]);

  const handleChatButtonClick = (event) => {
    // On mobile, the main button only toggles the menu open/close;
    // it never navigates away itself. WhatsApp/Call happen via the sub-icons.
    if (window.matchMedia("(max-width: 767px)").matches) {
      event.preventDefault();
      setContactMenuOpen((open) => !open);
      // Prevent the tapped <a> from retaining browser focus, which would
      // otherwise keep :focus-within styles active on some mobile browsers.
      event.currentTarget.blur();
    }
  };

  return (
    <BrowserRouter>
      <ScrollToHash />
      <style>{floatingWidgetCss}</style>
      <Header />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/course-executive-architect" element={<ExecutiveArchitectPage />} />
        <Route path="/course-executive-interior" element={<ExecutiveInteriorPage />} />
        <Route path="/customise-your-program" element={<CustomizeYourProgramPage />} />
        <Route path="/customize-your-program" element={<CustomizeYourProgramPage />} />
        <Route path="/contactus" element={<ContactUsPage />} />
        <Route path="/jobs" element={<JobsPage />} />
        <Route path="/hire" element={<HireTalentPage />} />
        <Route path="/enroll" element={<EnrollPage />} />
        <Route path="/partner-college" element={<PartnerCollegeForm />} />
        <Route path="/trainer-application" element={<TrainerApplicationForm />} />
        <Route path="/customise" element={<Navigate to="/customise-your-program" replace />} />
        <Route path="/customize" element={<Navigate to="/customize-your-program" replace />} />
        <Route path="/courses" element={<Navigate to="/course-executive-architect" replace />} />
        <Route path="/contact-us" element={<Navigate to="/contactus" replace />} />
        <Route path="/hire-talent" element={<Navigate to="/hire" replace />} />
        <Route path="/customize-program-application" element={<Navigate to="/customise-your-program" replace />} />
        <Route path="/programeafee" element={<Navigate to="/course-executive-architect" replace />} />
        <Route path="/programeidfee" element={<Navigate to="/course-executive-interior" replace />} />
        {/* <Route path="/ipdi-program" element={<Ipdiprogrampageredesign />} /> */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <Footer />
      <div
        ref={widgetRef}
        className={`floating-contact-widget ${showFloatingWidget ? "is-visible-mobile" : ""} ${
          contactMenuOpen ? "menu-open" : ""
        }`}
        aria-label="Quick contact"
      >
        <div className="floating-contact-menu">
          <div className="floating-contact-item">
            <span className="floating-contact-label">WhatsApp us</span>
            <a
              className="floating-contact-action"
              href="https://wa.me/918484924004"
              target="_blank"
              rel="noreferrer"
              aria-label="WhatsApp IPDI at +91 84849 24004"
            >
              <img src="/assets/img/whatsapp.svg" alt="" aria-hidden="true" />
            </a>
          </div>
          <div className="floating-contact-item">
            <span className="floating-contact-label">+91 84849 24004</span>
            <a
              className="floating-contact-action"
              href="tel:+918484924004"
              aria-label="Call IPDI at +91 84849 24004"
            >
              <img src="/assets/img/phone.svg" alt="" aria-hidden="true" />
            </a>
          </div>
        </div>
        <a
          className="floating-contact-chat"
          href="https://wa.me/918484914004"
          target="_blank"
          rel="noreferrer"
          aria-label="Message IPDI on WhatsApp"
          onClick={handleChatButtonClick}
        >
          <MessageSquare size={25} strokeWidth={2} aria-hidden="true" />
        </a>
      </div>
    </BrowserRouter>
  );
}

export default App;